'use client';

import { useLocale, useTranslations } from 'next-intl';
import type { MatchWithRating } from './types';
import { renderDelta } from './utils';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate, formatRating, formatTime } from '@/lib/utils';
import PlayerAvatar from '@/components/player/PlayerAvatar';
import { Link } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { useOutcomeLabel } from './matchOutcomeLabel';
import { completedGamesWon } from './formatScoreLine';

interface MatchCardProps {
  match: MatchWithRating;
  showBothPlayers?: boolean;
  showRatings?: boolean;
  player1?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_photo: string | null;
    current_rating: number | null;
    rating_delta?: number | null;
  } | null;
  player2?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_photo: string | null;
    current_rating: number | null;
    rating_delta?: number | null;
  } | null;
}

export function MatchCard({
  match,
  showBothPlayers = false,
  showRatings = true,
  player1,
  player2,
}: MatchCardProps) {
  const {
    dateISO,
    opponent,
    opponentUsername,
    result,
    venue,
    competition,
    myRating,
    myDelta,
    oppRating,
    oppDelta,
    isAdjustment,
    gameScores,
    hasScorecard,
    isWalkover,
    outcome,
    retiredPlayerId,
    winnerId,
    matchStatus,
    appState,
    hideRating,
    hideProfileLink,
    player1Roster,
    opponentRoster,
  } = match;

  const locale = useLocale();
  const t = useTranslations('Matches');
  const formatName = useFormatName();
  const outcomeLabel = useOutcomeLabel();

  const retired = outcome === 'retired' || !!retiredPlayerId;
  // A retirement's winner is winner_id, never whoever leads in games.
  const winnerSide: 'player1' | 'player2' | null =
    retired && winnerId
      ? winnerId === player1?.id
        ? 'player1'
        : winnerId === player2?.id
          ? 'player2'
          : null
      : null;
  const winnerAttr = (side: 'player1' | 'player2') =>
    winnerSide === side ? { 'data-winner': 'true' } : {};

  // Determine match status for badge
  const winnerless = isWinnerlessRow({ outcome, is_walkover: isWalkover });
  const isLive = !winnerless && matchStatus === 'in_progress';
  const isCompleted = !winnerless && matchStatus === 'completed';
  const isScheduled =
    !winnerless && (!matchStatus || matchStatus === 'scheduled' || (!isLive && !isCompleted));

  // Use smaller font only for long names (e.g. Malaysian/Indian names)
  const NAME_LENGTH_THRESHOLD = 15;

  // Format player names with script-aware ordering and fallbacks
  const player1DisplayName = player1
    ? formatName(player1.first_name, player1.last_name, player1.username)
    : '';
  // On a team surface (`hideProfileLink`) `opponent` is a team label, not a
  // person: it must not be split into first/last and re-ordered by the
  // script-aware formatter ("FC 台北" would come out as "台北FC"). Keep the
  // whole label as the "first name" so the avatar initials still work.
  const opponentIsTeamLabel = !!hideProfileLink;
  const opponentFirst = opponentIsTeamLabel
    ? opponent
    : opponent
      ? (opponent.split(' ')[0] ?? '')
      : '';
  const opponentLast = opponentIsTeamLabel
    ? ''
    : opponent
      ? opponent.split(' ').slice(1).join(' ')
      : '';
  const player2DisplayName =
    opponentIsTeamLabel && opponent
      ? opponent
      : opponent || opponentUsername
        ? formatName(opponentFirst, opponentLast, opponentUsername)
        : 'TBD';
  const isPlayer1LongName = player1DisplayName.length > NAME_LENGTH_THRESHOLD;
  const isPlayer2LongName = player2DisplayName.length > NAME_LENGTH_THRESHOLD;

  // Get current scores for live matches or final scores for completed
  const getCurrentScore = () => {
    // For live matches, resolve app_state
    if (isLive) {
      const resolved = tryResolveAppState(appState);
      if (resolved && resolved.games.length > 0) {
        return {
          player1: resolved.player_1_games,
          player2: resolved.player_2_games,
          gameScores: resolved.games.map((g) => ({
            player1: g.player_1_score,
            player2: g.player_2_score,
          })),
        };
      }
    }

    // For completed matches
    if (gameScores && gameScores.length > 0) {
      const { player1: totalPlayer1, player2: totalPlayer2 } = completedGamesWon(gameScores);
      return { player1: totalPlayer1, player2: totalPlayer2, gameScores };
    }
    return null;
  };

  const currentScore = getCurrentScore();

  return (
    <div
      className={
        `border-border bg-card rounded-lg border p-3 shadow-sm transition-all hover:shadow-md ` +
        (isAdjustment
          ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/20'
          : '')
      }
    >
      {/* Compact Header Row */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs">
          {/* `dateISO` is a timestamptz — hydration-safe (React #418). The time
              especially: it mismatches on EVERY non-UTC viewer, not just at the
              date boundary. */}
          <LocalDateTime
            iso={dateISO}
            locale={locale}
            format={formatDate}
            className="text-foreground font-medium"
          />
          <span className="text-muted">•</span>
          <LocalDateTime iso={dateISO} locale={locale} format={formatTime} className="text-muted" />
          {venue && (
            <>
              <span className="text-muted">•</span>
              <span className="text-muted truncate">{venue}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <Badge variant="destructive" className="animate-pulse px-1.5 py-0.5 text-[10px]">
              LIVE
            </Badge>
          )}
          {isScheduled && !isLive && (
            <Badge variant="outline" className="px-1.5 py-0.5 text-[10px]">
              SCHEDULED
            </Badge>
          )}
          {isCompleted && !isLive && (
            <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px]">
              COMPLETED
            </Badge>
          )}
        </div>
      </div>

      {/* Competition/Division */}
      {competition && (
        <div className="text-muted mb-2 truncate text-xs">
          {competition === 'Ranking Adjustment' ? t('rankingAdjustment') : competition}
        </div>
      )}

      {/* Main Content - Horizontal Player Layout */}
      {isAdjustment ? (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
            <div className="text-xs font-bold text-amber-700 dark:text-amber-300">ADJ</div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm leading-tight font-semibold">
              <span className="text-amber-700 dark:text-amber-300">{t('rankingAdjustment')}</span>
            </div>
          </div>
        </div>
      ) : showBothPlayers && player1 ? (
        <div className="space-y-2">
          {/* Players Row */}
          <div className="flex items-center gap-2">
            {/* Player 1 */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {hideProfileLink ? (
                <div className="flex-shrink-0">
                  <PlayerAvatar
                    player={
                      {
                        id: player1.id,
                        username: player1.username,
                        first_name: player1.first_name,
                        last_name: player1.last_name,
                        profile_photo: player1.profile_photo,
                      } as any
                    }
                    size="sm"
                  />
                </div>
              ) : (
                <Link href={`/user/${player1.username}`} className="flex-shrink-0" prefetch={false}>
                  <PlayerAvatar
                    player={
                      {
                        id: player1.id,
                        username: player1.username,
                        first_name: player1.first_name,
                        last_name: player1.last_name,
                        profile_photo: player1.profile_photo,
                      } as any
                    }
                    size="sm"
                  />
                </Link>
              )}
              <div className="min-w-0 flex-1">
                <div
                  {...winnerAttr('player1')}
                  className={
                    player1Roster
                      ? 'flex min-h-[2.25rem] flex-col justify-center'
                      : 'flex min-h-[2.25rem] items-center'
                  }
                >
                  <div
                    className={`leading-tight font-medium ${isPlayer1LongName ? 'text-sm' : 'text-base'}`}
                  >
                    {player1DisplayName}
                  </div>
                  {player1Roster && (
                    <div className="text-muted truncate text-xs">{player1Roster}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Center: Score/Status */}
            <div className="flex flex-shrink-0 flex-col items-center justify-center px-2">
              {winnerless ? (
                <div className="text-center">
                  <div className="text-muted text-sm font-medium">
                    {outcomeLabel(outcome, !!isWalkover)}
                  </div>
                </div>
              ) : (isLive || isCompleted) && currentScore ? (
                <div className="text-center">
                  <div className="text-lg font-bold">
                    {currentScore.player1} - {currentScore.player2}
                  </div>
                  {currentScore.gameScores && currentScore.gameScores.length > 0 && (
                    <div className="mt-1 flex flex-col items-center gap-0.5">
                      {currentScore.gameScores.map((game, index) => {
                        const isPlayer1Winner = game.player1 > game.player2;
                        return (
                          <div key={index} className="text-xs font-medium">
                            <span className={isPlayer1Winner ? 'font-bold' : 'text-muted'}>
                              {game.player1}
                            </span>
                            <span className="text-muted">-</span>
                            <span className={!isPlayer1Winner ? 'font-bold' : 'text-muted'}>
                              {game.player2}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {retired && <div className="text-muted mt-0.5 text-xs">{t('retiredShort')}</div>}
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-muted text-xs">vs</div>
                </div>
              )}
            </div>

            {/* Player 2 */}
            <div className="flex min-w-0 flex-1 flex-row-reverse items-center gap-2">
              {opponentUsername && opponentUsername !== 'unknown' ? (
                hideProfileLink ? (
                  <div className="flex-shrink-0">
                    <PlayerAvatar
                      player={
                        {
                          id: player2?.id || opponentUsername,
                          username: player2?.username || opponentUsername,
                          first_name: player2?.first_name || opponentFirst,
                          last_name: player2?.last_name || opponentLast,
                          profile_photo: player2?.profile_photo || match.opponentProfilePhoto,
                        } as any
                      }
                      size="sm"
                    />
                  </div>
                ) : (
                  <Link
                    href={`/user/${opponentUsername}`}
                    className="flex-shrink-0"
                    prefetch={false}
                  >
                    <PlayerAvatar
                      player={
                        {
                          id: player2?.id || opponentUsername,
                          username: player2?.username || opponentUsername,
                          first_name: player2?.first_name || opponentFirst,
                          last_name: player2?.last_name || opponentLast,
                          profile_photo: player2?.profile_photo || match.opponentProfilePhoto,
                        } as any
                      }
                      size="sm"
                    />
                  </Link>
                )
              ) : (
                <div className="bg-border flex h-8 w-8 items-center justify-center rounded-full">
                  <span className="text-xs">?</span>
                </div>
              )}
              <div className="min-w-0 flex-1 text-right">
                <div
                  {...winnerAttr('player2')}
                  className={
                    opponentRoster
                      ? 'flex min-h-[2.25rem] flex-col items-end justify-center'
                      : 'flex min-h-[2.25rem] items-center justify-end'
                  }
                >
                  <div
                    className={`leading-tight font-medium ${isPlayer2LongName ? 'text-sm' : 'text-base'}`}
                  >
                    {player2DisplayName}
                  </div>
                  {opponentRoster && (
                    <div className="text-muted truncate text-xs">{opponentRoster}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ratings Row */}
          {showRatings && !hideRating && (
            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <span className="text-muted">
                  {formatRating(player1?.current_rating || myRating)}
                </span>
                {renderDelta(
                  player1?.rating_delta !== null && player1?.rating_delta !== undefined
                    ? player1.rating_delta
                    : myDelta,
                )}
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-end gap-1">
                {oppRating && (
                  <>
                    <span className="text-muted">{formatRating(oppRating)}</span>
                    {renderDelta(oppDelta)}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Single Player View */}
          <div className="flex items-center gap-2">
            {hideProfileLink ? (
              <div className="flex-shrink-0">
                <PlayerAvatar
                  player={
                    {
                      id: opponentUsername || 'unknown',
                      username: opponentUsername,
                      first_name: opponentFirst,
                      last_name: opponentLast,
                      profile_photo: match.opponentProfilePhoto,
                    } as any
                  }
                  size="sm"
                />
              </div>
            ) : (
              <Link href={`/user/${opponentUsername}`} className="flex-shrink-0" prefetch={false}>
                <PlayerAvatar
                  player={
                    {
                      id: opponentUsername || 'unknown',
                      username: opponentUsername,
                      first_name: opponentFirst,
                      last_name: opponentLast,
                      profile_photo: match.opponentProfilePhoto,
                    } as any
                  }
                  size="sm"
                />
              </Link>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{player2DisplayName}</div>
              {hideProfileLink
                ? opponentRoster && (
                    <div className="text-muted truncate text-xs">{opponentRoster}</div>
                  )
                : opponentUsername && (
                    <div className="text-muted truncate text-xs">@{opponentUsername}</div>
                  )}
            </div>
            <div className="flex flex-shrink-0 flex-col items-end">
              {(isLive || isCompleted) && currentScore ? (
                <div className="flex flex-col items-end gap-0.5">
                  <div className="text-sm font-bold">
                    {currentScore.player1} - {currentScore.player2}
                  </div>
                  {currentScore.gameScores &&
                    currentScore.gameScores.length > 0 &&
                    currentScore.gameScores.map((game, index) => {
                      const isPlayer1Winner = game.player1 > game.player2;
                      return (
                        <div key={index} className="text-xs font-medium">
                          <span className={isPlayer1Winner ? 'font-bold' : 'text-muted'}>
                            {game.player1}
                          </span>
                          <span className="text-muted">-</span>
                          <span className={!isPlayer1Winner ? 'font-bold' : 'text-muted'}>
                            {game.player2}
                          </span>
                        </div>
                      );
                    })}
                  {retired && <div className="text-muted text-xs">{t('retiredShort')}</div>}
                </div>
              ) : (
                <div className="text-muted text-xs">{result}</div>
              )}
            </div>
          </div>

          {/* Ratings Row */}
          {showRatings && !hideRating && (
            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1">
                <span className="text-muted">{t('myRating')}:</span>
                <span className="text-foreground font-medium">{formatRating(myRating)}</span>
                {renderDelta(myDelta)}
              </div>
              {oppRating && (
                <div className="flex items-center gap-1">
                  <span className="text-muted">{t('opponentRating')}:</span>
                  <span className="text-foreground font-medium">{formatRating(oppRating)}</span>
                  {renderDelta(oppDelta)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scorecard Link at Bottom */}
      {(hasScorecard || isLive) && (
        <div className="border-border mt-2 border-t pt-2">
          <Link
            href={`/match/scorecard/${match.id}`}
            target="_blank"
            className="text-primary hover:text-primary/80 flex items-center justify-center text-xs font-medium transition-colors"
          >
            {t('viewScorecard')}
          </Link>
        </div>
      )}
    </div>
  );
}
